import { useMemo, useRef } from "react";
import { Keyboard } from "lucide-react";
import { Button } from "./ui/button";
import { fmtEUR, getDeviceGrid } from "@/lib/api";

/**
 * Inline replacement for the right-side ProductCatalogPanel that takes over
 * the same area when the comment selector is active. Mirrors the catalogue
 * layout pixel-for-pixel so the waiter's muscle memory stays intact:
 *
 *  ┌───────────────────────────────────────────────────────────┐
 *  │ Header strip:  [ΜΕ] [ΧΩΡΙΣ] [ΛΙΓΟ] [ΠΟΛΥ] …               │ ← same height
 *  ├───────────────────────────────────────────────────────────┤   as category
 *  │ Comment-tile grid (gridCols × gridRows from settings)     │   strip
 *  ├───────────────────────────────────────────────────────────┤
 *  │ Free-text input + ⌨ button | Ακύρωση | Καταχώρηση          │
 *  └───────────────────────────────────────────────────────────┘
 */
export default function CommentInlinePanel({
  tbl,
  commentDialog,
  setCommentDialog,
  comments,
  commentGroups,
  settings,
  COMMENT_PREFIXES,
  togglePrefix,
  toggleCommentTile,
  incrementSplit,
  decrementSplit,
  saveComment,
  oskOpen,
  setOskOpen,
}) {
  // Effective grid uses (in order of precedence):
  //   1. Per-device override in localStorage (set from Settings → Συσκευή)
  //   2. Global settings.comments_cols / comments_rows
  //   3. Hard-coded defaults
  const gridCols = Math.max(
    2,
    Math.min(12, getDeviceGrid("comments-cols") || settings?.comments_cols || 4),
  );
  const gridRows = Math.max(
    2,
    Math.min(8, getDeviceGrid("comments-rows") || settings?.comments_rows || 5),
  );
  const tileSizing = useMemo(() => {
    const colFactor = 5 / gridCols;
    const rowFactor = 4 / gridRows;
    const factor = Math.min(colFactor, rowFactor);
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    return {
      pad: clamp(Math.round(12 * factor), 6, 18),
      descPx: clamp(Math.round(18 * factor), 12, 26),
    };
  }, [gridCols, gridRows]);

  const item = useMemo(() => {
    // When editing a submitted batch item, ``submittedKey`` is
    // ``<batch_no>:<item_idx>``; we resolve it against the table batches so
    // the panel can show its description and respect its allowed groups.
    if (commentDialog.submittedKey) {
      const [bnS, iiS] = commentDialog.submittedKey.split(":");
      const bn = Number(bnS);
      const ii = Number(iiS);
      const b = (tbl?.submitted_batches || []).find((x) => x.batch_no === bn);
      return b?.items?.[ii] || null;
    }
    return (tbl?.open_items || [])[commentDialog.idx];
  }, [tbl, commentDialog.idx, commentDialog.submittedKey]);
  const allowedGroupIds = item?.comment_group_ids || [];
  // ⚠️ When the item has NO comment groups assigned, we deliberately
  // render an EMPTY group list — callers must instead pop a simple
  // free-text dialog (SimpleCommentDialog). Showing every group here
  // would surface unrelated categories on items that just have
  // ``allow_comment=true`` (user-reported bug).
  // Ιούν 2026: η σειρά εμφάνισης = η σειρά του comment_group_ids στο είδος
  // (ορίζεται με ▲▼ στην καρτέλα του είδους), όχι το global sort_order.
  const linkedGroups = (allowedGroupIds.length === 0
    ? []
    : commentGroups.filter((g) => allowedGroupIds.includes(g.id))
  ).sort((a, b) => allowedGroupIds.indexOf(a.id) - allowedGroupIds.indexOf(b.id));

  const close = () =>
    setCommentDialog({ open: false, idx: null, submittedKey: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });

  // Footer total respecting deduction flag.
  const totalEntries = Object.entries(commentDialog.selections);
  const total = totalEntries.reduce((s, [cid, qty]) => {
    const c = comments.find((x) => x.id === cid);
    const n = Number(qty) || 0;
    if (!c) return s;
    const sign = c.is_deduction ? -1 : 1;
    if (n > 0) return s + sign * Number(c.price_delta || 0) * n;
    if (n === -1 && c.is_deduction) return s - Number(c.price_delta || 0);
    return s;
  }, 0);

  const scrollRef = useRef(null);

  return (
    <section
      className="flex-1 h-full flex flex-col bg-slate-50 min-w-0"
      data-testid="comment-inline-panel"
    >
      {/* Header strip — prefix bar in the same place + size as the category strip */}
      <div className="px-4 py-3 border-b-2 border-blue-300 bg-blue-50">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-base font-bold text-blue-900 truncate" data-testid="cmt-inline-header">
            {commentDialog.submittedKey ? "Σχόλια (Διόρθωση)" : "Σχόλια"}: {item?.description || ""}
          </div>
          {commentDialog.activePrefix && (
            <span className="text-xs text-red-600 font-bold uppercase tracking-wider">
              ▶ Πρόθεμα: {commentDialog.activePrefix}
            </span>
          )}
        </div>
        <div
          className="grid gap-2 w-full"
          style={{
            gridTemplateColumns: `repeat(${COMMENT_PREFIXES.length}, minmax(0, 1fr))`,
            gridAutoRows: "64px",
          }}
          data-testid="cmt-prefix-bar"
        >
          {COMMENT_PREFIXES.map((p) => {
            const active = commentDialog.activePrefix === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePrefix(p)}
                data-testid={`cmt-prefix-${p}`}
                className={`rounded-lg text-base font-extrabold uppercase tracking-wider border-2 transition-all active:scale-[0.97] flex items-center justify-center ${
                  active
                    ? "bg-red-600 text-white border-red-700 shadow-lg ring-4 ring-red-300"
                    : p === "EXTRA"
                      ? "bg-white border-red-400 hover:bg-red-50 text-red-600"
                      : "bg-white border-slate-300 hover:bg-slate-100 text-slate-700"
                }`}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tile grid area — same gridCols / sizing as products */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin p-4 space-y-4">
          {linkedGroups.length === 0 && (
            <div className="text-center text-slate-400 py-10" data-testid="cmt-empty">
              Δεν υπάρχουν ομάδες σχολίων αντιστοιχισμένες στο είδος.
            </div>
          )}
          {linkedGroups.map((g) => {
            const groupComments = comments
              .filter((c) => c.group_id === g.id)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            if (groupComments.length === 0) return null;
            const perRow = !!settings?.comments_subgroup_per_row;
            // ── Render helper for a single tile (reused in both modes) ──
            const renderTile = (c) => {
              const qty = commentDialog.selections[c.id] || 0;
              const selected = qty !== 0;
              const pref = commentDialog.prefixes?.[c.id] || null;
              const bg = c.color || "#94a3b8";
              const baseLabel = pref ? `${pref} ${c.name}` : c.name;
              const label =
                qty === -1
                  ? `ΧΩΡΙΣ ${c.name}`
                  : qty > 1
                    ? `${qty}× ${baseLabel}`
                    : baseLabel;
              if (c.splittable) {
                return (
                  <div
                    key={c.id}
                    data-testid={`cmt-tile-${c.id}`}
                    className={`relative rounded-lg border-2 overflow-hidden font-bold leading-tight flex shadow-sm ${
                      selected ? "ring-4 ring-offset-2 ring-blue-600 shadow-lg" : ""
                    }`}
                    style={{ background: bg, color: "#fff", borderColor: bg, padding: 0 }}
                  >
                    <button
                      type="button"
                      onClick={() => decrementSplit(c)}
                      data-testid={`cmt-tile-${c.id}-minus`}
                      disabled={qty === -1}
                      className="w-12 h-full grid place-items-center text-2xl font-bold bg-black/20 hover:bg-black/30 active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <div
                      className="flex-1 grid place-items-center px-1 text-center break-words"
                      style={{ fontSize: `${tileSizing.descPx}px` }}
                    >
                      {label}
                      {qty > 0 && Number(c.price_delta) > 0 && (
                        <div className="text-xs font-mono opacity-90 mt-1">
                          {c.is_deduction ? "−" : "+"}
                          {fmtEUR(c.price_delta * qty)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => incrementSplit(c)}
                      data-testid={`cmt-tile-${c.id}-plus`}
                      className="w-12 h-full grid place-items-center text-2xl font-bold bg-black/20 hover:bg-black/30 active:scale-95 transition"
                    >
                      +
                    </button>
                  </div>
                );
              }
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCommentTile(c)}
                  data-testid={`cmt-tile-${c.id}`}
                  className={`relative rounded-lg border-2 shadow-sm hover:shadow-lg hover:brightness-110 active:scale-[0.97] transition-all overflow-hidden font-bold leading-tight ${
                    selected ? "ring-4 ring-offset-2 ring-blue-600 shadow-lg" : ""
                  }`}
                  style={{
                    background: bg,
                    color: "#fff",
                    borderColor: bg,
                    padding: `${tileSizing.pad}px`,
                  }}
                >
                  {selected && (
                    <div className="absolute top-1 right-1 bg-white rounded-full w-5 h-5 grid place-items-center text-blue-600 font-bold text-xs shadow">
                      ✓
                    </div>
                  )}
                  <div
                    className="text-center line-clamp-3 w-full"
                    style={{ fontSize: `${tileSizing.descPx}px` }}
                  >
                    {label}
                  </div>
                  {Number(c.price_delta) > 0 && (
                    <div className="absolute bottom-1 right-1 text-xs font-mono bg-black/30 rounded px-1.5 py-0.5">
                      {c.is_deduction ? "−" : "+"}
                      {fmtEUR(c.price_delta)}
                    </div>
                  )}
                </button>
              );
            };

            const gridStyle = {
              gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
              gridAutoRows: `minmax(${Math.max(
                48,
                Math.round(140 - gridCols * 8),
              )}px, calc((100vh - 400px) / ${gridRows}))`,
            };

            // ── per-row mode: bucket per subgroup, each on its own grid row(s)
            if (perRow) {
              const buckets = [];
              const map = new Map();
              groupComments.forEach((c) => {
                const key = c.radio_group || "__plain__";
                if (!map.has(key)) {
                  map.set(key, { key, items: [] });
                  buckets.push(map.get(key));
                }
                map.get(key).items.push(c);
              });
              return (
                <div key={g.id} data-testid={`cmt-group-${g.id}`}>
                  <div className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 border-b border-slate-200 pb-1">
                    {g.name}
                  </div>
                  {buckets.map((b) => (
                    <div
                      key={b.key}
                      className="mb-2 grid gap-2 w-full"
                      style={gridStyle}
                      data-testid={`cmt-bucket-${b.key}`}
                    >
                      {b.items.map(renderTile)}
                    </div>
                  ))}
                </div>
              );
            }

            // ── continuous-packing mode (default): single flat grid per group
            return (
              <div key={g.id} data-testid={`cmt-group-${g.id}`}>
                <div className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 border-b border-slate-200 pb-1">
                  {g.name}
                </div>
                <div className="grid gap-2 w-full" style={gridStyle}>
                  {groupComments.map(renderTile)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom bar: free-text input + cancel / save */}
      <div className="border-t-2 border-slate-300 bg-slate-50 px-4 py-3 space-y-2">
        <div className="flex items-center gap-2" data-testid="cmt-freetext-wrap">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 shrink-0">
            Ελεύθερο σχόλιο:
          </label>
          <input
            type="text"
            value={commentDialog.freeText || ""}
            onChange={(e) => setCommentDialog((d) => ({ ...d, freeText: e.target.value }))}
            placeholder="π.χ. χωρίς αλάτι, ψημένο μέτριο..."
            data-testid="cmt-freetext-input"
            className="flex-1 h-11 px-3 rounded-lg border-2 border-slate-300 bg-white text-sm focus:border-blue-500 focus:outline-none"
            maxLength={120}
          />
          <button
            type="button"
            onClick={() => setOskOpen(true)}
            data-testid="cmt-freetext-osk-btn"
            title="Άνοιγμα πληκτρολογίου οθόνης"
            className="h-11 w-12 grid place-items-center rounded-lg border-2 border-slate-300 bg-white hover:bg-blue-50 active:scale-95 transition-all"
          >
            <Keyboard size={20} className="text-slate-700" />
          </button>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="font-bold text-slate-700">{totalEntries.length}</span>
            <span className="text-slate-500"> επιλογές</span>
            {total !== 0 && (
              <span
                className={`ml-3 font-mono font-bold ${
                  total < 0 ? "text-red-600" : "text-emerald-700"
                }`}
              >
                {total < 0 ? "−" : "+"} {fmtEUR(Math.abs(total))}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-12 px-6 text-base font-bold border-2"
              onClick={close}
              data-testid="comment-cancel"
            >
              Ακύρωση
            </Button>
            <Button
              className="h-12 px-8 bg-blue-600 hover:bg-blue-700 text-white font-bold text-base"
              onClick={saveComment}
              data-testid="comment-save"
            >
              Καταχώρηση
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
